import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, IconButton, Tooltip, Snackbar, Alert, Typography,
} from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { CardWrapper, SectionTitle, ButtonOutlined } from '../../UI';
import IdentificacaoProcesso from '../Historico/IdentificacaoProcesso';
import ListaHistorico from '../Historico/ListaHistorico';
import DialogExpandirHistorico from '../Historico/DialogExpandirHistorico';
import TableAntecedentesAtivos from '../Historico/TableAntecedentesAtivos';
import BlocoAnotacao from '../Historico/BlocoAnotacao';
import MudancaFaseEnvio from '../Historico/MudancaFaseEnvio';
import DialogAgendarCompromisso from '../Historico/DialogAgendarCompromisso';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Histórico.
 *
 * Duas colunas de largura próxima (~1:1, como no print de referência da
 * story): à esquerda o card "Histórico" (Identificação do processo — Etapa
 * 1; Bloco de Anotação — Etapa 5; Mudança de Fase e Envio — Etapa 8; grid
 * "Antecedentes Ativos" somente-leitura — Etapa 4, posicionada por último
 * porque o print de referência não a mostra entre Identificação e Bloco de
 * Anotação — todos dentro do MESMO card), à direita a lista real de
 * Ocorrências (Etapa 2 — `ListaHistorico.jsx`) com o botão "EXPANDIR"
 * (Etapa 3 — `DialogExpandirHistorico.jsx`, mesmo `historicos` já
 * carregado, sem fetch novo).
 *
 * `abrirExpandirHistorico` é estado puramente local (não sobe ao container)
 * — mesmo princípio do padrão real (AW2-189): não afeta nenhum outro
 * estado, não dispara chamada nenhuma por si só.
 */
export default function AbaHistorico({
  processo,
  limparCamposHistorico,
  onChangeLimparCamposHistorico,
  historicos,
  carregandoMaisHistoricos,
  desabilitarFixarHistorico,
  erroFixarHistorico,
  onScrollFimHistoricos,
  onFixarHistorico,
  onFecharErroFixarHistorico,
  antecedentesAtivos,
  tiposHistoricoOptions,
  onAnotarSucesso,
  sinalizacaoInfo,
  onFaseAlterada,
  tiposAgendamentosOptions,
  tiposTitulosAgendamentosOptions,
  onAgendamentoAlterado,
  deveResetarCamposHistorico,
}) {
  const [abrirExpandirHistorico, setAbrirExpandirHistorico] = useState(false);
  const [agendarCompromissoAberto, setAgendarCompromissoAberto] = useState(false);

  // Defesa em profundidade: trocar de processo com o dialog de Agendar
  // Compromisso aberto não deveria ser alcançável via UI normal (o backdrop
  // do MUI Dialog bloqueia clique em outra linha da grid), mas fechar aqui
  // de qualquer forma evita mostrar/editar o agendamento de um processo
  // errado caso algum caminho de navegação futuro contorne o backdrop.
  useEffect(() => {
    setAgendarCompromissoAberto(false);
  }, [processo?.id]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: 'clamp(10px, 0.8vw, 22px)',
        alignItems: 'start',
      }}
    >
      <CardWrapper>
        <SectionTitle>Histórico</SectionTitle>
        <IdentificacaoProcesso
          processo={processo}
          limparCamposHistorico={limparCamposHistorico}
          onChangeLimparCamposHistorico={onChangeLimparCamposHistorico}
        />

        <BlocoAnotacao
          processo={processo}
          sinalizacaoInfo={sinalizacaoInfo}
          antecedentesAtivos={antecedentesAtivos}
          tiposHistoricoOptions={tiposHistoricoOptions}
          onSucesso={onAnotarSucesso}
          deveResetarCampos={deveResetarCamposHistorico}
        />

        <MudancaFaseEnvio
          processo={processo}
          sinalizacaoInfo={sinalizacaoInfo}
          antecedentesAtivos={antecedentesAtivos}
          onSucesso={onFaseAlterada}
          deveResetarCampos={deveResetarCamposHistorico}
        />

        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700, color: C.navy, mt: 2.5, mb: 1,
          }}
        >
          Antecedentes Ativos
        </Typography>
        <TableAntecedentesAtivos
          antecedentes={antecedentesAtivos}
        />
      </CardWrapper>

      <CardWrapper>
        <SectionTitle
          right={(
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ButtonOutlined size="small" onClick={() => setAbrirExpandirHistorico(true)}>EXPANDIR</ButtonOutlined>
              <Tooltip title="Agendar Compromisso" arrow>
                <IconButton size="small" sx={{ color: C.navy }} onClick={() => setAgendarCompromissoAberto(true)}>
                  <EventOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        >
          Ocorrências
        </SectionTitle>

        <ListaHistorico
          historicos={historicos}
          carregandoMais={carregandoMaisHistoricos}
          desabilitarFixar={desabilitarFixarHistorico}
          onScrollFim={onScrollFimHistoricos}
          onFixarHistorico={onFixarHistorico}
        />
      </CardWrapper>

      <DialogExpandirHistorico
        open={abrirExpandirHistorico}
        onClose={() => setAbrirExpandirHistorico(false)}
        historicos={historicos}
        carregandoMais={carregandoMaisHistoricos}
        onScrollFim={onScrollFimHistoricos}
      />

      <DialogAgendarCompromisso
        open={agendarCompromissoAberto}
        processo={processo}
        antecedentesAtivos={antecedentesAtivos}
        tiposAgendamentosOptions={tiposAgendamentosOptions}
        tiposTitulosAgendamentosOptions={tiposTitulosAgendamentosOptions}
        onClose={() => setAgendarCompromissoAberto(false)}
        onSucesso={onAgendamentoAlterado}
      />

      <Snackbar
        open={Boolean(erroFixarHistorico)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={onFecharErroFixarHistorico}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErroFixarHistorico}>
          {erroFixarHistorico}
        </Alert>
      </Snackbar>
    </Box>
  );
}
AbaHistorico.propTypes = {
  processo: PropTypes.object,
  limparCamposHistorico: PropTypes.bool.isRequired,
  onChangeLimparCamposHistorico: PropTypes.func.isRequired,
  historicos: PropTypes.shape({
    content: PropTypes.array,
    last: PropTypes.bool,
  }),
  carregandoMaisHistoricos: PropTypes.bool,
  desabilitarFixarHistorico: PropTypes.bool,
  erroFixarHistorico: PropTypes.string,
  onScrollFimHistoricos: PropTypes.func.isRequired,
  onFixarHistorico: PropTypes.func.isRequired,
  onFecharErroFixarHistorico: PropTypes.func.isRequired,
  antecedentesAtivos: PropTypes.arrayOf(PropTypes.object),
  tiposHistoricoOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.string,
  })),
  onAnotarSucesso: PropTypes.func.isRequired,
  sinalizacaoInfo: PropTypes.object,
  onFaseAlterada: PropTypes.func.isRequired,
  tiposAgendamentosOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.string,
  })),
  tiposTitulosAgendamentosOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.string,
  })),
  onAgendamentoAlterado: PropTypes.func.isRequired,
  deveResetarCamposHistorico: PropTypes.bool.isRequired,
};
AbaHistorico.defaultProps = {
  processo: null,
  historicos: null,
  carregandoMaisHistoricos: false,
  desabilitarFixarHistorico: false,
  sinalizacaoInfo: null,
  erroFixarHistorico: '',
  antecedentesAtivos: null,
  tiposHistoricoOptions: [],
  tiposAgendamentosOptions: [],
  tiposTitulosAgendamentosOptions: [],
};
